import type { FileStoreService } from '@/services/abstract/poc/FileStoreService';
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import type { DeletedFileEntry } from '@/types/poc/deletedFiles';
import type { HeliaNode } from '@/types/poc/helia';
import type { Logger } from '@/types/Logger';

/** What caused this reconcile to run — surfaced in logs for traceability. */
export type ReconcileDeletedFilesTrigger = 'login' | 'refreshMessage';

interface ReconcileDeletedFilesResult {
  scanned: number;
  unpinnedCount: number;
}

/**
 * Unpins an entry's file and file-key CIDs so their blocks become reclaimable,
 * isolating per-CID failures so one bad CID cannot strand the rest of the batch.
 * Returns how many of the two CIDs were actually pinned locally and unpinned.
 */
async function unpinEntry(
  fileStoreService: FileStoreService,
  node: HeliaNode,
  entry: DeletedFileEntry,
  log: Logger
): Promise<number> {
  let unpinned = 0;
  for (const cid of [entry.fileCid, entry.fileKeyCid]) {
    try {
      const wasPinned = await fileStoreService.unpinLocalFile(node, cid);
      if (wasPinned) {
        unpinned += 1;
      }
      log.debug('Processed deleted-file CID', { sixId: entry.sixId, cid, wasPinned });
    } catch (error) {
      log.warn('Failed to unpin deleted-file CID — skipping', {
        sixId: entry.sixId,
        cid,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
  return unpinned;
}

/**
 * Fetches the user's server-side deleted-files list, unpins any of those files still
 * held locally, then runs Helia garbage collection once to reclaim the freed blocks
 * (plus any previously-orphaned unpinned blocks). Idempotent and safe to run
 * repeatedly (on login and on the `refreshFilesToDeleteList` message); a
 * not-yet-started P2P node is a no-op.
 *
 * @see mydocs/proposals/deleted-files-local-cleanup.md
 */
export const reconcileDeletedFilesThunk = createAppAsyncThunk<
  ReconcileDeletedFilesResult,
  ReconcileDeletedFilesTrigger
>(
  'poc/reconcileDeletedFiles',
  async (trigger, { extra: { sixDetailService, pocServices, loggerService }, rejectWithValue }) => {
    const log = loggerService.createLogger({ namespace: 'six3ro:cleanup:deleted-files' });

    try {
      const node = pocServices.p2pStackManagerService.getHeliaNode();
      if (node === null) {
        log.info('Skipping reconcile — Helia node not ready', { trigger });
        return { scanned: 0, unpinnedCount: 0 };
      }

      log.info('Requesting deleted-files list from API', { trigger });
      const entries = await sixDetailService.fetchDeletedFiles();
      log.info('Received deleted-files list', { trigger, entries: entries.length });

      if (entries.length === 0) {
        return { scanned: 0, unpinnedCount: 0 };
      }

      let unpinnedCount = 0;
      for (const entry of entries) {
        unpinnedCount += await unpinEntry(pocServices.fileStoreService, node, entry, log);
      }

      // One global sweep reclaims the blocks we just unpinned plus any previously
      // orphaned unpinned blocks — cheaper and more thorough than per-file deletes.
      await pocServices.fileStoreService.runGarbageCollection(node);

      log.info('Reconcile complete', { trigger, scanned: entries.length, unpinnedCount });
      return { scanned: entries.length, unpinnedCount };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      log.error('Reconcile failed', { trigger, error: message });
      return rejectWithValue({ message });
    }
  }
);
